import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { observe } from './observe';
import {
  requestIdleCallback,
  cancelIdleCallback,
} from './requestIdleCallback';

type UseIntersectionObserverInit = Pick<
IntersectionObserverInit,
'rootMargin' | 'root'
>;

type UseIntersection = { disabled?: boolean; } & UseIntersectionObserverInit & {
  rootRef?: React.RefObject<HTMLElement> | null;
} ;
const hasIntersectionObserver = typeof IntersectionObserver !== 'undefined';
export function useIntersection<T extends Element>({
  rootRef,
  rootMargin,
  disabled,
}: UseIntersection): [(element: T | null) => void, boolean] {
  const isDisabled: boolean = disabled || !hasIntersectionObserver;
  const unobserve = useRef<Function>();
  const [visible, setVisible] = useState(false);
  const [root, setRoot] = useState(rootRef ? rootRef.current : null);
  const setRef = useCallback((el: T | null) => {
    if (unobserve.current) {
      unobserve.current();
      unobserve.current = undefined;
    }
    if (isDisabled || visible) return;
    if (el && el.tagName) {
      unobserve.current = observe(el, (isVisible) => isVisible && setVisible(isVisible), { root, rootMargin });
    }
  }, [isDisabled, root, rootMargin, visible]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!hasIntersectionObserver) {
      if (!visible) {
        const idleCallback = requestIdleCallback(() => setVisible(true));
        return () => cancelIdleCallback(idleCallback);
      }
    }
  }, [visible]);
  useEffect(() => {
    if (rootRef) setRoot(rootRef.current);
  }, [rootRef]);

  return [setRef, visible] as const;
}
